import React, { useState, useEffect } from 'react';
import { 
  X, User, Mail, Calendar, Users, MessageSquare, Star,
  Shield, Edit2, Save, Ban, UserCheck, Trash2,
  Activity
} from 'lucide-react';
import { Modal, Button, Input, ProfileAvatar, LoadingSpinner } from '../ui';
import ConfirmationModal from './ConfirmationModal';
import { toast } from '../ui/Toaster';
import { adminUserService } from '../../services/adminUserService';

interface UserProfileData {
  id: number;
  username: string;
  email: string;
  profilePhoto: string | null;
  bio: string | null;
  birthdate: string;
  age: number;
  gender: string;
  socialPreference: number;
  drugUsePreference: string | null;
  musicalPreferences: string | null;
  isAdmin: boolean;
  isSuspended: boolean;
  isPremium: boolean;
  suspendedAt: string | null;
  createdAt: string;
  updatedAt: string;
  extraGroupSlots: boolean;
  chatEnhancements: boolean;
  premiumExpiresAt: string | null;
  statistics: {
    ownedGroups: number;
    memberGroups: number;
    totalMessages: number;
    directMessages: number;
    createdEvents: number;
    eventAttendances: number;
    reportsReceived: number;
    blockedByUsers: number;
    recentActivity: {
      messages: number;
      groupJoins: number;
      eventCreations: number;
    };
  };
  groups: Array<{
    id: number;
    name: string;
    description: string | null;
    role: 'owner' | 'member';
    status: string;
    joinedAt: string | null;
    memberCount?: number;
    messageCount?: number;
    owner?: { id: number; username: string };
    event?: { id: number; title: string; slug: string } | null;
  }>;
  events: Array<{
    id: number;
    title: string;
    status: string;
    eventType: string;
    startDate: string;
    createdAt: string;
    _count: {
      attendees: number;
    };
  }>;
}

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onUserUpdated?: () => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onClose,
  userId,
  onUserUpdated
}) => {
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    bio: '',
    isAdmin: false,
    extraGroupSlots: false,
    chatEnhancements: false
  });

  // Confirmation modals
  const [suspendModal, setSuspendModal] = useState<{
    isOpen: boolean;
    suspend: boolean;
    loading: boolean;
  }>({
    isOpen: false,
    suspend: false,
    loading: false
  });

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    loading: boolean;
  }>({
    isOpen: false,
    loading: false
  });

  useEffect(() => {
    if (isOpen && userId) {
      loadUserProfile();
    }
  }, [isOpen, userId]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const profile = await adminUserService.getUserProfile(userId) as UserProfileData;
      setUserProfile(profile);
      
      // Initialize edit form
      setEditForm({
        username: profile.username,
        email: profile.email,
        bio: profile.bio || '',
        isAdmin: profile.isAdmin,
        extraGroupSlots: profile.extraGroupSlots,
        chatEnhancements: profile.chatEnhancements
      });
    } catch (error: any) {
      console.error('Failed to load user profile:', error);
      toast.error('Fout', 'Kon gebruikersprofiel niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (!userProfile) return;

    try {
      setEditLoading(true);
      
      await adminUserService.updateUser(userProfile.id.toString(), {
        username: editForm.username.trim(),
        email: editForm.email.trim().toLowerCase(),
        bio: editForm.bio.trim() || null,
        isAdmin: editForm.isAdmin,
        extraGroupSlots: editForm.extraGroupSlots,
        chatEnhancements: editForm.chatEnhancements
      });

      toast.success('Succes', 'Gebruikersprofiel bijgewerkt');
      setIsEditing(false);
      
      // Reload profile data
      await loadUserProfile();
      
      // Notify parent component
      onUserUpdated?.();
    } catch (error: any) {
      console.error('Failed to update user:', error);
      toast.error('Fout', error.message || 'Kon gebruiker niet bijwerken');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!userProfile) return;

    try {
      setSuspendModal(prev => ({ ...prev, loading: true }));
      
      await adminUserService.suspendUser(userProfile.id, suspendModal.suspend);
      
      toast.success(
        'Succes', 
        `Gebruiker ${suspendModal.suspend ? 'geschorst' : 'schorsing opgeheven'}`
      );
      setSuspendModal({ isOpen: false, suspend: false, loading: false });
      
      // Reload profile data
      await loadUserProfile();
      onUserUpdated?.();
    } catch (error: any) {
      console.error('Failed to change user suspension:', error);
      toast.error('Fout', error.message || 'Kon schorsingsstatus niet wijzigen');
      setSuspendModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteUser = async () => {
    if (!userProfile) return;

    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));
      
      await adminUserService.deleteUser(userProfile.id);
      
      toast.success('Succes', 'Gebruiker succesvol verwijderd');
      setDeleteModal({ isOpen: false, loading: false });
      
      // Close modal and notify parent
      onClose();
      onUserUpdated?.();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      toast.error('Fout', error.message || 'Kon gebruiker niet verwijderen');
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACCEPTED': return 'sf-badge-lime';
      case 'PENDING': return 'sf-badge-cyan';
      case 'REJECTED': return 'sf-badge-pink';
      default: return 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-body';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <div className="flex items-center justify-between p-6 border-b border-white/[.08]">
          <div className="flex items-center space-x-3">
            <User className="h-6 w-6 text-neon-cyan" />
            <h2 className="text-xl font-bold text-fg-bright">
              {loading ? 'Laden...' : `Gebruikersprofiel - ${userProfile?.username}`}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            {!loading && userProfile && (
              <>
                {isEditing ? (
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setEditForm({
                          username: userProfile.username,
                          email: userProfile.email,
                          bio: userProfile.bio || '',
                          isAdmin: userProfile.isAdmin,
                          extraGroupSlots: userProfile.extraGroupSlots,
                          chatEnhancements: userProfile.chatEnhancements
                        });
                      }}
                      disabled={editLoading}
                    >
                      Annuleren
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleEditSave}
                      disabled={editLoading}
                      className="flex items-center space-x-1"
                    >
                      {editLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      <span>Opslaan</span>
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="flex items-center space-x-1"
                  >
                    <Edit2 className="h-4 w-4" />
                    <span>Bewerken</span>
                  </Button>
                )}
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/[.06] rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-fg-muted" />
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : userProfile ? (
            <div className="space-y-6">
              {/* User Header */}
              <div className="sf-item p-4">
                <div className="flex justify-center mb-4">
                  <ProfileAvatar user={userProfile} size="xl" />
                </div>
                <div className="text-center space-y-3">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    {isEditing ? (
                      <Input
                        value={editForm.username}
                        onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                        className="text-xl font-bold"
                        placeholder="Gebruikersnaam"
                      />
                    ) : (
                      <h3 className="text-xl font-bold text-fg-bright">{userProfile.username}</h3>
                    )}
                    {userProfile.isAdmin && (
                      <Shield className="h-5 w-5 text-neon-cyan" />
                    )}
                    {userProfile.isPremium && (
                      <Star className="h-5 w-5 text-neon-lime" />
                    )}
                  </div>
                  
                  <div className="flex items-center justify-center space-x-4 text-sm text-fg-muted mb-2">
                    <div className="flex items-center space-x-1">
                      <Mail className="h-4 w-4 text-neon-cyan" />
                      {isEditing ? (
                        <Input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                          className="text-sm"
                          placeholder="Email"
                        />
                      ) : (
                        <span>{userProfile.email}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4 text-neon-cyan" />
                      <span>{userProfile.age} jaar</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-center space-x-2 mb-3">
                    {userProfile.isSuspended ? (
                      <span className="sf-badge-pink">
                        Geschorst
                      </span>
                    ) : (
                      <span className="sf-badge-lime">
                        Actief
                      </span>
                    )}

                    {isEditing ? (
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editForm.isAdmin}
                          onChange={(e) => setEditForm(prev => ({ ...prev, isAdmin: e.target.checked }))}
                          className="h-4 w-4 rounded accent-neon-pink"
                        />
                        <span className="text-sm text-fg-body">Administrator</span>
                      </label>
                    ) : userProfile.isAdmin && (
                      <span className="sf-badge-cyan">
                        Admin
                      </span>
                    )}

                    {userProfile.isPremium && (
                      <span className="inline-flex items-center rounded-lg bg-neon-violet/[.12] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-neon-violet">
                        Premium
                      </span>
                    )}
                  </div>

                  {/* Bio */}
                  <div>
                    {isEditing ? (
                      <textarea
                        value={editForm.bio}
                        onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                        placeholder="Bio (optioneel)"
                        rows={3}
                        className="sf-input resize-none"
                      />
                    ) : userProfile.bio ? (
                      <p className="text-fg-body">{userProfile.bio}</p>
                    ) : (
                      <p className="text-fg-muted italic">Geen bio</p>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                {!isEditing && (
                  <div className="flex flex-col space-y-2">
                    <Button
                      variant={userProfile.isSuspended ? "primary" : "outline"}
                      size="sm"
                      onClick={() => setSuspendModal({ 
                        isOpen: true, 
                        suspend: !userProfile.isSuspended, 
                        loading: false 
                      })}
                      className="flex items-center space-x-1"
                    >
                      {userProfile.isSuspended ? <UserCheck className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                      <span>{userProfile.isSuspended ? 'Opheffen' : 'Schorsen'}</span>
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteModal({ isOpen: true, loading: false })}
                      className="flex items-center space-x-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Verwijderen</span>
                    </Button>
                  </div>
                )}
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="sf-card p-4">
                  <div className="flex items-center space-x-2">
                    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-neon-gradient text-ink">
                      <Users className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-fg-bright">
                        {userProfile.statistics.ownedGroups + userProfile.statistics.memberGroups}
                      </p>
                      <p className="sf-label-mono">Groepen</p>
                    </div>
                  </div>
                </div>
                <div className="sf-card p-4">
                  <div className="flex items-center space-x-2">
                    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-accept-gradient text-ink">
                      <MessageSquare className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-fg-bright">{userProfile.statistics.totalMessages}</p>
                      <p className="sf-label-mono">Berichten</p>
                    </div>
                  </div>
                </div>
                <div className="sf-card p-4">
                  <div className="flex items-center space-x-2">
                    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-avatar-violet text-ink">
                      <Calendar className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-fg-bright">{userProfile.statistics.createdEvents}</p>
                      <p className="sf-label-mono">Evenementen</p>
                    </div>
                  </div>
                </div>
                <div className="sf-card p-4">
                  <div className="flex items-center space-x-2">
                    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-avatar-pink text-ink">
                      <Activity className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-fg-bright">
                        {userProfile.statistics.recentActivity.messages}
                      </p>
                      <p className="sf-label-mono">Berichten (30d)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Details - Vertical Layout */}
              <div className="space-y-6">
                {/* Profile Details */}
                <div className="sf-card p-4">
                  <h4 className="font-semibold text-fg-bright mb-3">Profiel details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-fg-muted">Geslacht:</span>
                      <span className="font-medium text-fg">{userProfile.gender}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-fg-muted">Sociale voorkeur:</span>
                      <span className="font-medium text-fg">{userProfile.socialPreference}/9</span>
                    </div>
                    {userProfile.drugUsePreference && (
                      <div className="flex justify-between">
                        <span className="text-fg-muted">Middelengebruik:</span>
                        <span className="font-medium text-fg">{userProfile.drugUsePreference}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-fg-muted">Lid sinds:</span>
                      <span className="font-medium text-fg">{formatDate(userProfile.createdAt)}</span>
                    </div>
                  </div>
                </div>

                {/* Premium Features */}
                <div className="sf-card p-4">
                  <h4 className="font-semibold text-fg-bright mb-3">Premium status</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-fg-muted">Extra groep slots:</span>
                      {isEditing ? (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.extraGroupSlots}
                            onChange={(e) => setEditForm(prev => ({ ...prev, extraGroupSlots: e.target.checked }))}
                            className="h-4 w-4 rounded accent-neon-pink"
                          />
                          <span className="text-sm text-fg-body">{editForm.extraGroupSlots ? 'Actief' : 'Niet actief'}</span>
                        </label>
                      ) : (
                        <span className={
                          userProfile.extraGroupSlots
                            ? 'sf-badge-lime'
                            : 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-muted'
                        }>
                          {userProfile.extraGroupSlots ? 'Actief' : 'Niet actief'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-fg-muted">Chat verbeteringen:</span>
                      {isEditing ? (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.chatEnhancements}
                            onChange={(e) => setEditForm(prev => ({ ...prev, chatEnhancements: e.target.checked }))}
                            className="h-4 w-4 rounded accent-neon-pink"
                          />
                          <span className="text-sm text-fg-body">{editForm.chatEnhancements ? 'Actief' : 'Niet actief'}</span>
                        </label>
                      ) : (
                        <span className={
                          userProfile.chatEnhancements
                            ? 'sf-badge-lime'
                            : 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-muted'
                        }>
                          {userProfile.chatEnhancements ? 'Actief' : 'Niet actief'}
                        </span>
                      )}
                    </div>
                    {userProfile.premiumExpiresAt && (
                      <div className="flex justify-between">
                        <span className="text-fg-muted">Verloopt op:</span>
                        <span className="font-medium text-fg">{formatDate(userProfile.premiumExpiresAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Groups */}
              {userProfile.groups.length > 0 && (
                <div className="sf-card p-4">
                  <h4 className="font-semibold text-fg-bright mb-3">Groepen ({userProfile.groups.length})</h4>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {userProfile.groups.map((group, index) => (
                      <div key={`${group.id}-${group.role}-${index}`} className="sf-item flex items-center justify-between p-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h5 className="font-medium text-fg-bright truncate">{group.name}</h5>
                            <span className={`${getStatusColor(group.status)}`}>
                              {group.role === 'owner' ? 'Eigenaar' : group.status}
                            </span>
                          </div>
                          {group.description && (
                            <p className="text-sm text-fg-muted truncate mt-1">{group.description}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-1 text-xs text-fg-muted">
                            {group.memberCount && (
                              <span>{group.memberCount} leden</span>
                            )}
                            {group.messageCount && (
                              <span>{group.messageCount} berichten</span>
                            )}
                            {group.joinedAt && (
                              <span>Lid sinds {formatDate(group.joinedAt)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Events */}
              {userProfile.events.length > 0 && (
                <div className="sf-card p-4">
                  <h4 className="font-semibold text-fg-bright mb-3">Evenementen ({userProfile.events.length})</h4>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {userProfile.events.map((event) => (
                      <div key={event.id} className="sf-item flex items-center justify-between p-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h5 className="font-medium text-fg-bright truncate">{event.title}</h5>
                            <span className={`${getStatusColor(event.status)}`}>
                              {event.status}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 mt-1 text-xs text-fg-muted">
                            <span>{event.eventType}</span>
                            <span>{event._count.attendees} geïnteresseerd</span>
                            <span>Aangemaakt {formatDate(event.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="italic text-fg-muted">Gebruiker niet gevonden</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Suspend/Unsuspend Confirmation */}
      <ConfirmationModal
        isOpen={suspendModal.isOpen}
        onClose={() => !suspendModal.loading && setSuspendModal({ isOpen: false, suspend: false, loading: false })}
        onConfirm={handleSuspendUser}
        title={suspendModal.suspend ? "Gebruiker schorsen" : "Schorsing opheffen"}
        message={`Weet je zeker dat je "${userProfile?.username}" wilt ${suspendModal.suspend ? 'schorsen' : 'de schorsing wilt opheffen'}?`}
        confirmText={suspendModal.suspend ? "Schorsen" : "Opheffen"}
        type={suspendModal.suspend ? "warning" : "info"}
        loading={suspendModal.loading}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => !deleteModal.loading && setDeleteModal({ isOpen: false, loading: false })}
        onConfirm={handleDeleteUser}
        title="Gebruiker verwijderen"
        message={`Weet je zeker dat je "${userProfile?.username}" permanent wilt verwijderen? Dit verwijdert alle data en kan niet ongedaan worden gemaakt.`}
        confirmText="Verwijderen"
        type="danger"
        loading={deleteModal.loading}
      />
    </>
  );
};